import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { RouterLink } from '@angular/router';

interface RegisterForm {
  firstname: string;
  surname: string;
  email: string;
  cardnumber: string;
  password: string;
  confirmPassword: string;
}

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [CommonModule, TranslateModule, FormsModule, RouterLink],
  template: `
    <div class="auth-page">
      <div class="auth-container">
        <div class="auth-card">
          <div class="auth-card__header">
            <div class="auth-card__icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="#1b8354" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="#1b8354" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <h1 class="auth-card__title">{{ "Register.title" | translate }}</h1>
            <p class="auth-card__subtitle">{{ "Register.subtitle" | translate }}</p>
          </div>

          @if (submitted()) {
            <div class="auth-success">
              <div class="auth-success__icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="#1b8354" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M22 4L12 14.01L9 11.01" stroke="#1b8354" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <h2>{{ "Register.successTitle" | translate }}</h2>
              <p>{{ "Register.successMessage" | translate }}</p>
              <a routerLink="/home" class="auth-btn auth-btn--primary" style="text-decoration:none;display:inline-block;margin-top:12px;">{{ "Nav.Home" | translate }}</a>
            </div>
          } @else {
            <form #registerForm="ngForm" (ngSubmit)="onSubmit(registerForm)" class="auth-form" novalidate>
              <div class="auth-form__row">
                <div class="auth-form__group">
                  <label for="firstname" class="auth-form__label">{{ "Register.firstname" | translate }} <span class="required">*</span></label>
                  <input id="firstname" type="text" class="auth-form__input"
                    name="firstname" [(ngModel)]="form.firstname" required
                    [placeholder]="'Register.firstnamePlaceholder' | translate"
                    #firstnameField="ngModel"
                    [class.is-invalid]="firstnameField.invalid && (firstnameField.dirty || submitAttempted())">
                </div>
                <div class="auth-form__group">
                  <label for="surname" class="auth-form__label">{{ "Register.surname" | translate }} <span class="required">*</span></label>
                  <input id="surname" type="text" class="auth-form__input"
                    name="surname" [(ngModel)]="form.surname" required
                    [placeholder]="'Register.surnamePlaceholder' | translate"
                    #surnameField="ngModel"
                    [class.is-invalid]="surnameField.invalid && (surnameField.dirty || submitAttempted())">
                </div>
              </div>

              <div class="auth-form__group">
                <label for="email" class="auth-form__label">{{ "Register.email" | translate }} <span class="required">*</span></label>
                <input id="email" type="email" class="auth-form__input"
                  name="email" [(ngModel)]="form.email" required email
                  [placeholder]="'Register.emailPlaceholder' | translate"
                  #emailField="ngModel"
                  [class.is-invalid]="emailField.invalid && (emailField.dirty || submitAttempted())">
              </div>

              <div class="auth-form__group">
                <label for="cardnumber" class="auth-form__label">{{ "Register.cardnumber" | translate }}</label>
                <input id="cardnumber" type="text" class="auth-form__input"
                  name="cardnumber" [(ngModel)]="form.cardnumber"
                  [placeholder]="'Register.cardnumberPlaceholder' | translate">
              </div>

              <div class="auth-form__group">
                <label for="reg-password" class="auth-form__label">{{ "Register.password" | translate }} <span class="required">*</span></label>
                <div class="auth-form__password-wrapper">
                  <input id="reg-password" [type]="showPassword() ? 'text' : 'password'" class="auth-form__input"
                    name="password" [(ngModel)]="form.password" required minlength="8"
                    [placeholder]="'Register.passwordPlaceholder' | translate"
                    #passwordField="ngModel"
                    [class.is-invalid]="passwordField.invalid && (passwordField.dirty || submitAttempted())">
                  <button type="button" class="auth-form__eye-btn" (click)="showPassword.set(!showPassword())">
                    @if (showPassword()) {
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
                    } @else {
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/></svg>
                    }
                  </button>
                </div>
              </div>

              <div class="auth-form__group">
                <label for="confirmPassword" class="auth-form__label">{{ "Register.confirmPassword" | translate }} <span class="required">*</span></label>
                <input id="confirmPassword" type="password" class="auth-form__input"
                  name="confirmPassword" [(ngModel)]="form.confirmPassword" required
                  [placeholder]="'Register.confirmPasswordPlaceholder' | translate"
                  [class.is-invalid]="submitAttempted() && form.password !== form.confirmPassword">
              </div>

              @if (errorKey()) {
                <div class="auth-error">{{ errorKey() | translate }}</div>
              }

              <button type="submit" class="auth-btn auth-btn--primary" [class.loading]="isLoading()">
                @if (isLoading()) {
                  <span class="auth-spinner"></span>
                }
                {{ "Register.submit" | translate }}
              </button>

              <div class="auth-form__footer">
                <a routerLink="/home" (click)="openLogin()" class="auth-form__link">{{ "Register.alreadyHaveAccount" | translate }}</a>
              </div>
            </form>
          }
        </div>
      </div>
    </div>
  `,
  styles: [`
    .auth-page {
      min-height: 100vh;
      background: linear-gradient(135deg, #f0faf5 0%, #e8f5ee 50%, #f5f7fa 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 40px 16px;
    }
    .auth-container { width: 100%; max-width: 560px; }
    .auth-card {
      background: #fff;
      border-radius: 16px;
      box-shadow: 0 4px 24px rgba(0,0,0,0.08);
      padding: 40px;
    }
    .auth-card__header { text-align: center; margin-bottom: 32px; }
    .auth-card__icon {
      width: 72px; height: 72px;
      background: #f0faf5; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      margin: 0 auto 16px;
    }
    .auth-card__title { font-size: 24px; font-weight: 700; color: #111; margin: 0 0 8px; }
    .auth-card__subtitle { font-size: 14px; color: #666; margin: 0; }
    .auth-form__row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    @media (max-width: 480px) { .auth-form__row { grid-template-columns: 1fr; } .auth-card { padding: 24px; } }
    .auth-form__group { margin-bottom: 16px; }
    .auth-form__label { display: block; font-size: 13px; font-weight: 600; color: #333; margin-bottom: 6px; }
    .required { color: #e53e3e; }
    .auth-form__input {
      width: 100%; padding: 10px 14px; border: 1.5px solid #e2e8f0; border-radius: 8px;
      font-size: 14px; color: #111; outline: none; transition: border-color 0.2s;
      box-sizing: border-box;
      &:focus { border-color: #1b8354; box-shadow: 0 0 0 3px rgba(27,131,84,0.08); }
      &.is-invalid { border-color: #e53e3e; }
    }
    .auth-form__password-wrapper { position: relative; }
    .auth-form__password-wrapper .auth-form__input { padding-inline-end: 42px; }
    .auth-form__eye-btn {
      position: absolute; inset-inline-end: 12px; top: 50%; transform: translateY(-50%);
      background: none; border: none; cursor: pointer; color: #666; padding: 0;
    }
    .auth-btn {
      width: 100%; padding: 12px; border-radius: 8px; font-size: 15px; font-weight: 600;
      cursor: pointer; border: none; transition: all 0.2s; display: flex; align-items: center; justify-content: center; gap: 8px;
      &--primary { background: #1b8354; color: #fff;
        &:hover { background: #16a362; }
        &.loading { opacity: 0.7; pointer-events: none; }
      }
    }
    .auth-spinner {
      width: 18px; height: 18px; border: 2px solid rgba(255,255,255,0.4);
      border-top-color: #fff; border-radius: 50%; animation: spin 0.7s linear infinite;
    }
    @keyframes spin { to { transform: rotate(360deg); } }
    .auth-error { background: #fff5f5; border: 1px solid #fed7d7; color: #c53030; border-radius: 8px; padding: 10px 14px; font-size: 13px; margin-bottom: 16px; }
    .auth-form__footer { text-align: center; margin-top: 20px; }
    .auth-form__link { font-size: 13px; color: #1b8354; text-decoration: none; &:hover { text-decoration: underline; } }
    .auth-success { text-align: center; padding: 20px 0;
      &__icon { margin-bottom: 16px; }
      h2 { font-size: 20px; font-weight: 700; color: #1b8354; margin-bottom: 8px; }
      p { color: #555; line-height: 1.6; }
    }
  `]
})
export class RegisterComponent {
  form: RegisterForm = { firstname: '', surname: '', email: '', cardnumber: '', password: '', confirmPassword: '' };
  errorKey = signal('');
  isLoading = signal(false);
  submitted = signal(false);
  submitAttempted = signal(false);
  showPassword = signal(false);

  openLogin() {
    // navigate to home which will open login dropdown
  }

  onSubmit(ngForm: NgForm): void {
    this.submitAttempted.set(true);

    if (ngForm.invalid) {
      this.errorKey.set('Register.errorRequired');
      return;
    }
    if (this.form.password !== this.form.confirmPassword) {
      this.errorKey.set('Register.errorPasswordMatch');
      return;
    }

    this.errorKey.set('');
    this.isLoading.set(true);

    // Since POST /patrons is staff-only, we simulate a "submitted" state.
    // In production, this would call a backend proxy endpoint.
    setTimeout(() => {
      this.isLoading.set(false);
      this.submitted.set(true);
    }, 1000);
  }
}

